import {
  HStack,
  Select,
  Input,
  StackProps,
  InputGroup,
  InputLeftAddon,
  Icon,
  Box,
  FormLabel,
} from '@chakra-ui/react';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import ptBr from 'date-fns/locale/pt-BR';
import ReactAsyncSelect from 'react-select/async';
import 'react-datepicker/dist/react-datepicker.css';
import { components, IndicatorProps, OptionTypeBase } from 'react-select';
import { HiChevronDown } from 'react-icons/hi';
import { useAsyncSelectStyles } from './useAsyncSelectStyles';
import { maskCpf } from '../../utils/formatters/handleMask';

registerLocale('pt-BR', ptBr);

export type TableFilterOption = {
  label: string;
  value?: string | number;
  maskType?: string;
};

interface ITableFiltersProps extends StackProps {
  filterBy?: string;
  filterByOptions?: TableFilterOption[];
  filterInputType?: 'input' | 'asyncSelect' | 'select' | 'datepicker';
  filterSelectOptions?: TableFilterOption[];
  loadAsyncOptions?: (
    inputValue: string,
    callback?: (options: TableFilterOption[]) => void,
  ) => void;
  onChangeFilterBy?: (filter: string) => void;
  onFilter?: (value: string) => void;
  onFilterAsync?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onSearch?: (searchValue: string) => void;
  onSort?: (sort: string) => void;
  order?: 'ASC' | 'DESC';
  searchLabel?: string;
  sortBy?: string;
  sortByOptions?: TableFilterOption[];
}

export const TableFilters = ({
  filterBy,
  filterByOptions = [],
  filterInputType = 'input',
  filterSelectOptions = [],
  loadAsyncOptions,
  onChangeFilterBy,
  onFilter,
  onFilterAsync,
  onOrder,
  onSearch,
  onSort,
  order = 'ASC',
  searchLabel,
  sortBy,
  sortByOptions = [],
  ...rest
}: ITableFiltersProps): JSX.Element => {
  const [selectedDate, setSelectedDate] = useState<Date | null>();

  const orderOptions = useMemo(
    () => [
      { label: 'Crescente', value: 'ASC' },
      { label: 'Decrescente', value: 'DESC' },
    ],
    [],
  );

  const handleDateChange = useCallback(
    (value: Date | null) => {
      if (onFilter) {
        setSelectedDate(value);
        onFilter(value ? value.toISOString().split('T')[0] : '');
      }
    },
    [onFilter],
  );

  const DropdownIndicator = (
    props: IndicatorProps<OptionTypeBase, boolean>,
  ): React.ReactElement => (
    <components.DropdownIndicator {...props}>
      <Icon as={HiChevronDown} fontSize="19" />
    </components.DropdownIndicator>
  );

  const renderFilterInput = useCallback(
    (
      filter: (value: string) => void,
      type: 'input' | 'asyncSelect' | 'select' | 'datepicker',
      loadOptions?: (inputValue: string) => void,
    ) => {
      switch (type) {
        case 'asyncSelect':
          return (
            <Box w="96">
              <ReactAsyncSelect
                cacheOptions
                isClearable
                onChange={(e) => filter(e?.value)}
                loadOptions={loadOptions}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                styles={useAsyncSelectStyles}
                placeholder=""
                loadingMessage={() => 'Carregando...'}
                noOptionsMessage={({ inputValue }) =>
                  !inputValue ? 'Começe a digitar...' : 'Nenhum encontrado'
                }
                components={{ DropdownIndicator }}
              />
            </Box>
          );

        case 'select':
          return (
            <Select
              w="96"
              borderColor="gray.300"
              borderLeftWidth={0}
              bg="gray.100"
              focusBorderColor="blue.300"
              variant="outline"
              onChange={(e) => filter(e.target.value)}
            >
              {filterSelectOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          );

        case 'datepicker':
          return (
            <ReactDatePicker
              placeholderText="Selecione uma data"
              dateFormat="dd/MM/yyyy"
              isClearable
              locale="pt-BR"
              selected={selectedDate}
              onChange={(value) => handleDateChange(value as Date | null)}
              todayButton="Hoje"
              customInput={
                <Input
                  w="96"
                  borderColor="gray.300"
                  borderLeftWidth={0}
                  bg="gray.100"
                  focusBorderColor="blue.300"
                  variant="outline"
                  size="sm"
                  onKeyUp={(e) => {
                    e.currentTarget.maxLength = 10;

                    e.currentTarget.value = e.currentTarget.value
                      .replace(/\D/g, '')
                      .replace(/(\d{2})(\d)/, '$1/$2')
                      .replace(/(\d{2})(\d)/, '$1/$2');
                  }}
                />
              }
            />
          );

        default: {
          const handleInputMask = (
            originalEvent: ChangeEvent<HTMLInputElement>,
          ): void => {
            const maskType = filterByOptions.find(
              (option) => option.value === filterBy,
            )?.maskType;

            // "clona" evento e por referência atualiza o evento original quando alterado o clone
            const event = originalEvent;

            switch (maskType) {
              case 'cpf': {
                event.target.value = maskCpf(event.target.value);
                event.target.maxLength = 14;

                break;
              }

              default:
                break;
            }
          };

          return (
            <Input
              w="96"
              borderColor="gray.300"
              borderLeftWidth={0}
              bg="gray.100"
              focusBorderColor="blue.300"
              variant="outline"
              onChange={(e) => {
                handleInputMask(e);

                filter(e.target.value);
              }}
            />
          );
        }
      }
    },
    [
      filterBy,
      filterByOptions,
      filterSelectOptions,
      handleDateChange,
      selectedDate,
    ],
  );

  return (
    <HStack spacing="4" align="flex-end" {...rest}>
      {onSearch && (
        <Box>
          <FormLabel htmlFor="search">{searchLabel}</FormLabel>
          <Input
            w="96"
            name="search"
            borderColor="gray.300"
            bg="gray.100"
            focusBorderColor="blue.300"
            size="sm"
            variant="outline"
            onChange={(e) => onSearch(e.target.value)}
          />
        </Box>
      )}

      {!!onChangeFilterBy && (
        <Box>
          <FormLabel htmlFor="search-by">Buscar por</FormLabel>
          <InputGroup size="sm">
            <InputLeftAddon p="0">
              <Select
                name="search-by"
                variant="outline"
                borderColor="gray.300"
                focusBorderColor="blue.300"
                size="sm"
                value={filterBy}
                onChange={(e) => onChangeFilterBy(e.target.value)}
              >
                {filterByOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </InputLeftAddon>

            {filterInputType !== 'asyncSelect' &&
              !!onFilter &&
              renderFilterInput(onFilter, filterInputType)}

            {filterInputType === 'asyncSelect' &&
              !!onFilterAsync &&
              !!loadAsyncOptions &&
              renderFilterInput(
                onFilterAsync,
                filterInputType,
                loadAsyncOptions,
              )}
          </InputGroup>
        </Box>
      )}

      {(!!onSort || !!onOrder) && (
        <Box>
          <HStack>
            {!!onSort && (
              <Box>
                <FormLabel htmlFor="order-by">Ordenar por</FormLabel>
                <Select
                  name="order-by"
                  borderColor="gray.300"
                  bg="gray.100"
                  focusBorderColor="blue.300"
                  variant="outline"
                  size="sm"
                  value={sortBy}
                  onChange={(e) => onSort(e.target.value)}
                >
                  {sortByOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Box>
            )}

            {!!onOrder && (
              <Box>
                <FormLabel htmlFor="ordering">Ordenação</FormLabel>
                <Select
                  name="ordering"
                  borderColor="gray.300"
                  bg="gray.100"
                  focusBorderColor="blue.300"
                  variant="outline"
                  size="sm"
                  value={order}
                  onChange={(e) => onOrder(e.target.value as 'ASC' | 'DESC')}
                >
                  {orderOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </Box>
            )}
          </HStack>
        </Box>
      )}
    </HStack>
  );
};
